import * as d3 from 'd3'
import dagre from 'dagre'
import _ from 'underscore'
import { formatCallgraphNodeValue, formatValueWithSign } from './helper'

let Callgraph = function () {
    var svg = null

    var zoom = null

    var graph = null

    var nodeSize = { x: 280, y: 44 }

    var foldedNodeSize = { x: 280, y: 30 }

    var labelSize = { x: 55, y: 34 }

    var resized = false

    var selected = null

    var overallWt = 0

    let events = {}

    let asDelta = false

    let centerOffset = 0

    let memory = false

    var _scales = function () {
        return {
            color: d3.scaleLinear()
                .domain([0, Math.max(100, overallWt / 1000)])
                .range(['#777', '#f56954']),
            diffColor: d3.scaleLinear()
                .domain([-1, 1])
                .range(['#777', '#f56954']),
        }
    }

    var _calculateGraph = function (state) {
        var graph = new dagre.graphlib.Graph()
        var size = { x: 0, y: 0 }

        // These are 'necessary' intializations for dagre to work. We have no
        // use for them, though.
        graph.setGraph({})
        graph.setDefaultEdgeLabel(function () { return {} })

        for (let i = 0; i < state.nodes.length; i++) {
            let node = state.nodes[i]

            // Golden ration fibonacci numbers \o/
            node.width = nodeSize.x
            node.height = nodeSize.y

            graph.setNode(node.id, node)
        }

        for (let i = 0; i < state.edges.length; i++) {
            let edge = state.edges[i]

            edge.width = 16
            edge.height = 16

            graph.setEdge(edge.source, edge.target, edge)
        }

        // Setting layout options just does not work with the latest release of
        // Dagre – they are ignore even this is the documented way. There is
        // also no other way to access / set those properties.
        dagre.layout(graph, {
            ranksep: 34,
            nodesep: 44,
        })

        return {
            nodes: _.map(graph.nodes(), function (nodeId) {
                let node = graph.node(nodeId)

                node.x = Math.floor(node.x)
                node.y = Math.floor(node.y)

                size.x = Math.max(size.x, node.x + nodeSize.x + 44)
                size.y = Math.max(size.y, node.y + nodeSize.y + 44)

                return node
            }),
            edges: _.map(graph.edges(), function (edgeId) {
                return graph.edge(edgeId)
            }),
            size: size,
        }
    }

    var _addClass = function (nodes, className) {
        nodes.classed(className, true)
    }

    var _removeClass = function (nodes, className) {
        nodes.classed(className, false)
    }

    var _drawNodes = function (scales, nodes) {
        var g = d3.select(svg).selectAll('.nodes')
        var node = g.selectAll('.node.call').data(nodes.filter(node => node.type === 'call'), function (node) { return 'n_' + node.id })
        var newNode = node.enter()
        var group = newNode.append('g').attr('id', function (node) { return 'n_' + node.id })

        // New node
        group
            .attr('class', function (node) {
                return 'node ' + (node.type || '') + (node.id === selected ? ' selected ' : '')
            })
            .on('mouseover', function () {
                _addClass(d3.select(this), 'hover')
            })
            .on('mouseout', function () {
                _removeClass(d3.select(this), 'hover')
            })
            .on('click', function (node) {
                _removeClass(d3.select(svg).selectAll('.node.call'), 'selected')
                _addClass(d3.select(this), 'selected')

                events.onNodeSelected(node)
            })
            .attr('data-tooltip-id', 'callgraphTooltip')
            .attr('data-tooltip-content', node => [node.namespace, node.name].filter(entry => entry && entry.length > 0).join('\\'))

        group
            .append('rect')
            .attr('class', 'background')
            .attr('width', nodeSize.x + 10)
            .attr('height', nodeSize.y)

        // This elements "clips" the node description so it does not exceed the
        // available size
        group
            .append('clipPath')
            .attr('id', function (node) { return 'clip_' + node.id })
            .append('rect').attr('class', 'clipPath')
                .attr('width', nodeSize.x - 10)
                .attr('height', nodeSize.y - 10)

        group
            .append('text').attr('class', 'name')
            .attr('clip-path', function (node) { return 'url(#clip_' + node.id + ')' })

        group
            .append('text').attr('class', 'namespace')
            .attr('clip-path', function (node) { return 'url(#clip_' + node.id + ')' })
            .text(function (node) { return node.namespace })

        group
            .append('rect').attr('class', 'timeBackground')
            .attr('width', labelSize.x + 10)
            .attr('height', labelSize.y)

        // This elements "clips" the wall time text so it does not exceed the
        // available size
        group
            .append('clipPath')
            .attr('id', function (node) { return 'clip_time_' + node.id })
            .append('rect').attr('class', 'timeClipPath')
                .attr('width', labelSize.x - 2 + 10)
                .attr('height', labelSize.y - 2)

        group
            .append('text').attr('class', 'time')
            .attr('clip-path', function (node) { return 'url(#clip_time_' + node.id + ')' })

        group = node.merge(group)

        // Update existing node (set properties which might change)
        group.transition().duration(50)
            .attr('class', function (node) {
                return 'node ' + (node.type || '') + (node.id === selected ? ' selected ' : '')
            })

        group.select('text.name')
            .text(node => node.name)

        group.select('rect.background').transition().duration(50)
            .attr('x', function (node) { return node.x })
            .attr('y', function (node) { return node.y })
            .attr('class', function (node) {
                return 'background' +
                    ((node.excl_wt / overallWt) > 0.5 ? ' danger' :
                    (((node.excl_wt / overallWt) > 0.1 ? ' warning' : '')))
            })

        group.select('rect.markerBackground').transition().duration(50)
            .attr('x', function (node) { return node.x + nodeSize.x - 26 })
            .attr('y', function (node) { return node.y + nodeSize.y })

        group.select('rect.clipPath').transition().duration(50)
            .attr('x', function (node) { return node.x + 5 })
            .attr('y', function (node) { return node.y + 5 })

        group.select('text.name').transition().duration(50)
            .attr('x', function (node) { return node.x + nodeSize.x - labelSize.x - 10 })
            .attr('y', function (node) { return node.y + 17 })

        group.select('text.namespace').transition().duration(50)
            .attr('x', function (node) { return node.x + nodeSize.x - labelSize.x - 10 })
            .attr('y', function (node) { return node.y + 34 })

        group.select('text.marker').transition().duration(50)
            .attr('x', function (node) { return node.x + nodeSize.x - 26 + 2 })
            .attr('y', function (node) { return node.y + nodeSize.y + 13 })

        group.select('rect.timeBackground').transition().duration(50)
            .style('fill', function (node) {
                if (node.type && (node.type === 'changed')) {
                    return scales.diffColor(node.wt_diff)
                } else if (!node.type) {
                    return scales.color(node.wt / 1000)
                }
            })
            .attr('x', function (node) { return node.x + nodeSize.x - 5 - labelSize.x })
            .attr('y', function (node) { return node.y + nodeSize.y - 5 - labelSize.y })

        group.select('rect.timeClipPath').transition().duration(50)
            .attr('x', function (node) { return node.x + nodeSize.x - 5 - labelSize.x + 1 })
            .attr('y', function (node) { return node.y + nodeSize.y - 5 - labelSize.y + 1 })

        group.select('text.time').transition().duration(50)
            .attr('x', function (node) { return node.x + nodeSize.x - 5 - (labelSize.x / 2) + 5 })
            .attr('y', function (node) { return node.y + nodeSize.y - 5 - 10 })
            .text((node) => formatCallgraphNodeValue(node, memory, asDelta))

        // Exiting node, before merge to remove old nodes
        node.exit().remove()
    }

    var _drawFoldedNodes = function (scales, nodes) {
            var g = d3.select(svg).selectAll('.foldednodes')
            var node = g.selectAll('.node.folded').data(nodes.filter(node => node.type === 'folded'), function (node) { return 'n_' + node.id })
            var newNode = node.enter()
            var group = newNode.append('g').attr('id', function (node) { return 'n_' + node.id })

            // New node
            group
                .attr('class', function (node) {
                    return 'node ' + (node.type || '')
                })
                .on('mouseover', function () {
                    _addClass(d3.select(this), 'hover')
                })
                .on('mouseout', function () {
                    _removeClass(d3.select(this), 'hover')
                })
                .on('click', function (node) {
                    events.unfoldCalls(node.foldedNodes)
                })
                .attr('data-tooltip-id', 'callgraphTooltip')
                .attr('data-tooltip-content', node => node.name)

            // This elements "clips" the node description so it does not exceed the
            // available size
            group
                .append('clipPath')
                .attr('id', function (node) { return 'clip_' + node.id })
                .append('rect').attr('class', 'clipPath')
                    .attr('width', foldedNodeSize.x)
                    .attr('height', foldedNodeSize.y)

            group
                .append('text').attr('class', 'name')
                .attr('clip-path', function (node) { return 'url(#clip_' + node.id + ')' })

            group = node.merge(group)

            // Update existing node (set properties which might change)
            group.transition().duration(50)
                .attr('class', function (node) {
                    return 'node ' + (node.type || '')
                })

            group.select('text.name')
                .text(node => node.foldedNodes.length + ' folded calls (click to expand)')

            group.select('rect.clipPath').transition().duration(50)
                .attr('x', function (node) { return node.x + 5 })
                .attr('y', function (node) { return node.y + 5 })

            group.select('text.name').transition().duration(50)
                .attr('x', function (node) { return node.x + nodeSize.x - labelSize.x })
                .attr('y', function (node) { return node.y + 25 })

            // Exiting node, before merge to remove old nodes
            node.exit().remove()
        }

    var _drawEdges = function (scales, edges) {
        var g = d3.select(svg).selectAll('.edges')

        var edge = g.selectAll('.edge').data(edges, function (edge) { return 'e_' + edge.source + '_' + edge.target })
        var newEdge = edge.enter()
        var group = newEdge.append('g')

        var getPathForEdge = function (edge) {
            let pathString = 'M '

            for (let i = 0; i < edge.points.length; ++i) {
                pathString +=
                    (edge.points[i].x + nodeSize.x / 2) + ' ' +
                    (edge.points[i].y + nodeSize.y / 2) + ' L '
            }

            return pathString.substring(0, pathString.length - 2)
        }

        group
            .append('text').attr('class', 'calls')

        // New edge
        group
            .attr('class', 'edge')

        group
            .append('path').attr('class', 'arrow')
            .attr('marker-end', 'url(#arrow)')

        group = edge.merge(group)

        // Update existing edge
        group.select('path.arrow').transition().duration(50)
            .attr('d', getPathForEdge)

        group.select('text.calls').transition().duration(50)
            .attr('x', function (edge) { return edge.x + nodeSize.x / 2 })
            .attr('y', function (edge) { return edge.y + nodeSize.y / 2 + 12 })
            .text(edge =>
                formatValueWithSign(edge.ct, asDelta) + (edge.ct === 1 ? ' call' : ' calls') +
                ' · ' +
                formatCallgraphNodeValue(edge, memory, asDelta)
            )

        // Exiting edge, before merge to remove old nodes
        edge.exit().remove()
    }

    this.create = function (root, width, height, onNodeSelected, unfoldCalls, showAsDeltas) {
        events = { onNodeSelected, unfoldCalls }

        svg = root

        asDelta = showAsDeltas

        resized = false

        let d3Svg = d3.select(svg)
            .attr('class', 'callgraph')
            .attr('width', width)
            .attr('height', height)

        let defs = d3Svg.append('defs')
        let marker = defs.append('marker')

        marker.attr({
            id: 'arrow',
            viewBox: '0 -5 10 10',
            refX: 5,
            refY: 0,
            markerWidth: 4,
            markerHeight: 4,
            orient: 'auto',
        })

        marker.append('path')
                .attr('d', 'M0,-5L10,0L0,5')
                .attr('class', 'arrowHead')

        let container = d3Svg.append('g')
            .attr('class', 'container')

        container.append('g')
            .attr('class', 'nodes')

        container.append('g')
            .attr('class', 'foldednodes')

        container.append('g')
            .attr('class', 'edges')

        zoom = d3.zoom()
            .scaleExtent([8 / 50, 30 / 8])
            .on('zoom', function () {
                container.attr('transform', d3.event.transform)
            })
    }

    this.update = function (state, wt, selectedNode, centerOffsetPixels, useMemory, sizeUpdate = null) {
        if (selectedNode) {
            selected = selectedNode
        }

        if (sizeUpdate) {
            d3.select(svg)
                .attr('width', sizeUpdate.width)
                .attr('height', sizeUpdate.height)
        }

        centerOffset = centerOffsetPixels

        memory = useMemory

        graph = _calculateGraph(state)
        overallWt = wt

        d3.select(svg).call(zoom)

        _drawNodes(_scales(), graph.nodes)
        _drawFoldedNodes(_scales(), graph.nodes)
        _drawEdges(_scales(), graph.edges)

        if (!resized) {
            this.zoomFull()
            resized = true
        }
    }

    this.adaptScale = function (modifier) {
        zoom.scaleBy(d3.select(svg).transition(), modifier)
    }

    const translateToCenter = (position, dimension, totalDimension, scale, centerOffset) => {
        totalDimension -= centerOffset

        return (-((position * scale + ((dimension * scale) / 2)) - (totalDimension / 2)) + centerOffset) || 0
    }

    this.zoomHome = function () {
        const startNode = graph.nodes.find(node => node.name === 'main()')

        if (!startNode) {
            return
        }

        this.selectNode(startNode.id, 1)
        events.onNodeSelected(startNode)
    }

    this.zoomFull = function () {
        const dx = graph.size.x
        const dy = graph.size.y
        const viewPortWidth = parseFloat(d3.select(svg).attr('width'))
        const viewPortHeight = parseFloat(d3.select(svg).attr('height'))
        const startNode = graph.nodes.find(node => node.name === 'main()')

        if (!startNode) {
            return
        }

        const scale = 1 / Math.max(1, Math.min(8,
            Math.max(dx / viewPortWidth, (1.05 * dy) / viewPortHeight)))

        this.selectNode(startNode.id, scale, false)
    }

    this.selectNode = function (nodeId, scale = 1, highlightNode = true) {
        selected = nodeId
        if (!graph) {
            return
        }

        if (highlightNode) {
            _removeClass(d3.select(svg).selectAll('.node'), 'selected')
            _addClass(d3.select('#n_' + nodeId), 'selected')
        }

        let viewPortWidth = 0 + d3.select(svg).attr('width')
        let viewPortHeight = 0 + d3.select(svg).attr('height')

        const node = graph.nodes.find(node => node.id === nodeId)

        if (node) {
            d3.select(svg).transition()
                .call(zoom.transform,
                    d3.zoomIdentity
                        .translate(
                            translateToCenter(node.x, nodeSize.x, viewPortWidth, scale, centerOffset),
                            d3.min([translateToCenter(node.y, nodeSize.y, viewPortHeight, scale, 0), 0])
                        )
                        .scale(scale)
                )
        }
    }

    this.hoverNode = function (nodeId) {
        _addClass(d3.select('#n_' + nodeId), 'hover')
    }

    this.hoverNodeExit = function (nodeId) {
        _removeClass(d3.select('#n_' + nodeId), 'hover')
    }
}

export default Callgraph
